import styles from './AssistantPanel.module.css';

import type { FC } from 'react';
import type { ApiAssistantCarCheck, ApiAssistantSaleEvidence } from 'src/types/assistant';

import React from 'react';

import { Typography } from 'src/components/themed/Typography';

/**
 * The "Check this car" list under a car for sale (api#430; Tim's decisions of 2026-10-28 and
 * 2026-10-29): a search of the VIN, the paid reports on the providers' own sites, the official
 * checks of the dealer's country and, for a car built in North America, the US checks. Links only:
 * the person goes there; the platform buys no report and sees none. Without a VIN of a VIN's form
 * the list is one line, in Tim's words — and no line asks the person to contact the dealer, as the
 * platform has no chat between them.
 */

/** Only an https address of the api's list becomes a link: anything else is shown as no link at all. */
const isSafeLink = (url: string): boolean => {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
};

const ExternalLink: FC<{ href: string; label: string; detail?: string }> = ({
  href,
  label,
  detail,
}) =>
  isSafeLink(href) ? (
    <li>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${label} (opens in a new tab)`}
        className={styles.dealerLink}
      >
        <Typography as="span" color="white" variant="small">
          {label}
        </Typography>
      </a>
      {detail ? (
        <Typography as="span" color="lightWhite" variant="small">
          {` — ${detail}`}
        </Typography>
      ) : null}
    </li>
  ) : null;

/** "free; the VIN" — what a check costs and what the person enters there. */
const checkDetail = (check: ApiAssistantCarCheck): string =>
  `${check.about}; ${check.cost}; needs ${check.needs}`;

interface CarChecksProps {
  checks: NonNullable<ApiAssistantSaleEvidence['checks']>;
  vinStatus: ApiAssistantSaleEvidence['vin']['status'];
  title: string;
}

export const CarChecks: FC<CarChecksProps> = ({ checks, vinStatus, title }) => {
  const country = checks.dealerCountry?.name;

  return (
    <section className={styles.checks} aria-label={`Check this car: ${title}`}>
      <Typography
        as="p"
        color="lightWhite"
        variant="small"
        fontWeight="600"
        className={styles.checksTitle}
      >
        Check this car
      </Typography>
      {checks.vin === null ? (
        <Typography color="white" variant="small">
          {vinStatus === 'malformed'
            ? 'The dealer has stated a VIN that is not a valid one.'
            : 'The dealer has not stated the car’s VIN.'}
        </Typography>
      ) : (
        <>
          <ul className={styles.checkList}>
            {checks.searchUrl ? (
              <ExternalLink href={checks.searchUrl} label="Search this VIN on Google" />
            ) : null}
            {checks.reports.map((report) => (
              <ExternalLink
                key={report.provider}
                href={report.url}
                label={`History report on ${report.provider}`}
                detail="paid, on their site"
              />
            ))}
          </ul>
          <Typography color="white" variant="small">
            {country
              ? checks.official.length > 0
                ? `The dealer is in ${country}. Official checks there:`
                : `The dealer is in ${country}. ${checks.countryNote ?? `No official check of ${country} is known to the platform.`}`
              : 'Official checks depend on the country where the car is registered; the dealer has not stated their country.'}
          </Typography>
          {checks.official.length > 0 ? (
            <ul className={styles.checkList}>
              {checks.official.map((check) => (
                <ExternalLink
                  key={check.id}
                  href={check.url}
                  label={check.name}
                  detail={checkDetail(check)}
                />
              ))}
            </ul>
          ) : null}
          {checks.us.length > 0 ? (
            <>
              <Typography color="white" variant="small">
                Built in North America. Free US checks:
              </Typography>
              <ul className={styles.checkList}>
                {checks.us.map((check) => (
                  <ExternalLink
                    key={check.id}
                    href={check.url}
                    label={check.name}
                    detail={checkDetail(check)}
                  />
                ))}
              </ul>
            </>
          ) : null}
          <Typography color="lightWhite" variant="small">
            GAC does not see these reports: you buy and read them on the provider’s site.
          </Typography>
        </>
      )}
    </section>
  );
};
